import { useCallback } from 'react';
import { type Href, useLocalSearchParams, useRouter } from 'expo-router';
import { Platform } from 'react-native';

import { useIsNavigationReady } from '@sg/garnish';

import { useFeatureFlag } from '@order/LaunchDarkly';
import { useTelemetry } from '@order/Telemetry';

import {
  type MenuContentDeliveryDetailsData,
  type MenuContentOrderChannel,
} from '../../MenuContent.types';

// ─────────────────────────────────────────────────────────────────────────────

export const useMenuContentNavigation = (params: UseMenuContentNavigationParams) => {
  const { restaurantSlug, orderChannel, deliveryDetailsData } = params;

  const router = useRouter();
  const isNavigationReady = useIsNavigationReady();
  const routeParams = useLocalSearchParams<MaybeParamsWithCategorySlug>();
  const { trackEvent } = useTelemetry();
  const isNavbarV2Enabled = useFeatureFlag('CELS-5440-order-navbar-v2-enabled');

  const defaultActiveTargetId = routeParams.category;

  // ─── Helpers ─────────────────────────────────────────────────────────

  const changeLocation = useCallback(() => {
    trackEvent({ name: 'menu.change_location' });

    router.navigate(isNavbarV2Enabled ? '/location-selection' : '/locations');
  }, [isNavbarV2Enabled, router, trackEvent]);

  const generateCustomizationHref = useCallback(
    (productSlug: string): Href => {
      if (orderChannel === 'delivery') {
        const addressId = deliveryDetailsData?.addressId ?? '';

        return {
          pathname: '/delivery/[addressId]/[productSlug]',
          params: { addressId, productSlug },
        };
      }

      return {
        pathname: '/[restaurantSlug]/[productSlug]',
        params: { restaurantSlug, productSlug },
      };
    },
    [deliveryDetailsData?.addressId, orderChannel, restaurantSlug],
  );

  const setTargetParam = useCallback(
    (targetSlug: string) => {
      if (Platform.OS !== 'web' || !isNavigationReady) return;

      router.setParams({ category: targetSlug });
    },
    [isNavigationReady, router],
  );

  const openContactUs = useCallback(() => {
    router.navigate('/chat');
  }, [router]);

  return {
    defaultActiveTargetId,
    changeLocation,
    generateCustomizationHref,
    setTargetParam,
    openContactUs,
  };
};

// ─── Types ───────────────────────────────────────────────────────────────────

type UseMenuContentNavigationParams = {
  restaurantSlug: string;
  deliveryDetailsData?: MenuContentDeliveryDetailsData;
  orderChannel: MenuContentOrderChannel;
};

type MaybeParamsWithCategorySlug = { category?: string };
